// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { useGetLink } from "@insite/client-framework/Store/Links/LinksSelectors";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import Button, { ButtonPresentationProps, ButtonSizeVariants } from "@insite/mobius/Button";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import LazyImage, { LazyImageProps } from "@insite/mobius/LazyImage";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import getProp from "@insite/mobius/utilities/getProp";
import { useHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { CustomAppState } from "@keller/App/Store/Reducers";
import { color, layoutContainer, linkArrowStyle, mergeWithCss, xClassNames, xScreen } from "@keller/XDL/Utilities";
import parse from "html-react-parser";
import * as React from "react";
import { css, ThemeProps } from "styled-components";

export interface BlockCardProp {
    image?: string;
    mobileSpecificImage?: string;
    imagePosition?: "left" | "right";
    title: string;
    titleColor?: "black" | "white";
    description:
        | string
        | React.DetailedReactHTMLElement<{}, HTMLElement>
        | React.DetailedReactHTMLElement<{}, HTMLElement>[];
    descriptionColor?: "black" | "white";
    link: LinkFieldValue;
    buttonVariant?: "primary" | "secondary" | "tertiary" | "link";
    buttonLabel?: string;
    size: "small" | "medium";
    backgroundColor?: string;
    extendedStyles?: BlockCardStyles;
    buttonSizeVariant?: ButtonSizeVariants;
}

export interface BlockCardStyles {
    container?: InjectableCss;
    wrapper?: InjectableCss;
    infoWrapper?: InjectableCss;
    title?: TypographyProps;
    description?: TypographyProps;
    image?: LazyImageProps;
    link?: LinkPresentationProps;
    button?: ButtonPresentationProps;
}

export const blockCardStyles: BlockCardStyles = {
    container: {
        css: css`
            display: flex;
            box-shadow: ${getProp("theme.shadows.4")};

            ${({ backgroundColor, itemSize }: any) => css`
                ${backgroundColor &&
                css`
                    background-color: ${color(backgroundColor)};
                `};
                ${itemSize === "small" &&
                css`
                    max-width: 638px;
                    border-radius: 4px;
                `};
            `};
            ${xScreen("lte", "sm")} {
                ${({ itemSize }: any) => css`
                    ${itemSize === "small" &&
                    css`
                        max-width: 100%;
                    `};
                `};
            }
        `,
    },
    wrapper: {
        css: css`
            display: flex;
            justify-content: space-between;
            width: 100%;

            ${({ imagePosition, itemSize }: any) => css`
                ${imagePosition &&
                css`
                    flex-direction: ${imagePosition === "right" ? "row-reverse" : "row"};
                `};
                ${itemSize === "medium" &&
                css`
                    ${layoutContainer}
                `};
            `};

            ${xScreen("lte", "md")} {
                ${({ itemSize }: any) => css`
                    ${itemSize === "medium" &&
                    css`
                        flex-direction: column;
                        height: 100%;
                    `};
                `}
            }
            ${xScreen("lte", "xs")} {
                ${({ itemSize }: any) => css`
                    ${itemSize === "small" &&
                    css`
                        flex-direction: column;
                        height: 100%;
                    `};
                    ${itemSize === "medium" &&
                    css`
                        &&&& {
                            padding-left: 0;
                            padding-right: 0;
                        }
                    `};
                `}
            }
        `,
    },
    image: {
        css: css`
            flex-shrink: 0;
            ${({ itemSize, imagePosition }: any) => css`
                ${itemSize === "medium" &&
                css`
                        max-height: 357px;
                        min-height: 340px;
                        max-width: 893px;
                        margin-${imagePosition === "right" ? "left" : "right"}: auto;
                        flex: 1 1 auto;
                `}
                ${itemSize === "small" &&
                css`
                        width: 198px;
                        min-height: 211px;
                        margin-${imagePosition === "right" ? "left" : "right"}: auto;
                        border-radius: 0 4px 4px 0;
                    `};
            `};

            ${xScreen("lte", "md")} {
                ${({ imagePosition, itemSize }: any) => css`
                    ${itemSize === "medium" &&
                    css`
                            max-width: 100%;
                            width: 100%;
                            height: 100%;
                            max-height: 307px;
                            min-height: initial;
                            margin-${imagePosition === "right" ? "left" : "right"}: initial;
                        `};
                `}
            }
            ${xScreen("lte", "xs")} {
                ${({ imagePosition, itemSize }: any) => css`
                    ${itemSize === "small" &&
                    css`
                            width: 100%;
                            height: 100%;
                            max-height: 311px;
                            min-height: initial;
                            margin-${imagePosition === "right" ? "left" : "right"}: initial;
                            border-radius: 4px 4px 0 0;
                        `};
                `}
            }

            img {
                width: 100%;
                height: 100%;
                object-fit: cover;
            }
        `,
    },
    infoWrapper: {
        css: css`
            padding: 24px;
            margin: auto 0;
            ${({ imagePosition, itemSize }: any) => css`
                ${itemSize === "medium" &&
                css`
                    min-width: 440px;
                    max-width: 440px;
                    flex-shrink: 0;
                    padding-${imagePosition}: 52px;
                `};
            `}

            ${xScreen("lte", "md")} {
                ${({ itemSize }: any) => css`
                    ${itemSize === "medium" &&
                    css`
                        min-width: initial;
                        max-width: 100%;
                        flex-shrink: 0;
                        flex-grow: 1;
                        padding: 24px;
                    `};
                `}
            }

            ${xScreen("lte", "xs")} {
                padding: 16px;
            }
        `,
    },
    title: {
        as: "h2",
        weight: 900,
        css: css`
            margin-bottom: 15px;
        `,
    },
    description: {
        as: "p",
        size: "15px",
        lineHeight: "24px",
        weight: 400,
        css: css`
            margin-bottom: 30px;
        `,
    },
    link: {
        ...linkArrowStyle,
    },
};

type Props = BlockCardProp;

const BlockCard: React.FC<Props> = ({
    image,
    title,
    description,
    imagePosition,
    link,
    buttonVariant,
    buttonLabel,
    size,
    mobileSpecificImage,
    backgroundColor = "common.ui10",
    titleColor = "black",
    descriptionColor = "black",
    extendedStyles,
    buttonSizeVariant = "large",
}) => {
    const [styles] = React.useState(() => mergeWithCss(blockCardStyles, extendedStyles));
    const url = useGetLink(link).url;
    const history = useHistory();
    const isMobileScreen = useAppSelector(state => (state as CustomAppState).xCommon.isMobileScreen);

    const openLinkHandler = () => url && history.push(url);

    const elementProps = {
        itemSize: size,
        backgroundColor,
        titleColor,
        descriptionColor,
        imagePosition,
    };

    return (
        <StyledWrapper {...styles.container} {...elementProps} data-test-selector="block_card">
            <StyledWrapper {...styles.wrapper} {...elementProps}>
                {image && (
                    <LazyImage
                        src={isMobileScreen ? mobileSpecificImage || image : image}
                        altText={title}
                        {...styles.image}
                        {...elementProps}
                    />
                )}
                <StyledWrapper {...elementProps} {...styles.infoWrapper} data-test-selector="block_card_info">
                    <Typography
                        size={size === "small" ? 24 : 28}
                        lineHeight={size === "small" ? "30px" : "34px"}
                        data-test-selector="block_card_title"
                        color={titleColor}
                        {...elementProps}
                        {...styles.title}
                    >
                        {title}
                    </Typography>
                    <Typography
                        data-test-selector="block_card_description"
                        color={descriptionColor}
                        {...elementProps}
                        {...styles.description}
                    >
                        {typeof description === "string" ? parse(description, parserOptions) : description}
                    </Typography>
                    {url && buttonLabel && (
                        <>
                            {size === "medium" && buttonVariant !== "link" ? (
                                <Button
                                    {...styles.button}
                                    variant={buttonVariant}
                                    aria-label={buttonLabel}
                                    sizeVariant={buttonSizeVariant}
                                    onClick={openLinkHandler}
                                >
                                    {buttonLabel}
                                </Button>
                            ) : (
                                <Link onClick={openLinkHandler} aria-label={buttonLabel} {...styles.link}>
                                    {buttonLabel}
                                </Link>
                            )}
                        </>
                    )}
                </StyledWrapper>
            </StyledWrapper>
        </StyledWrapper>
    );
};

export default BlockCard;
